import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getRevisionTechniques } from "../../../../services/techniquesServices";

import {
  getUserFavorites,
  addFavorite,
  removeFavorite,
} from "../../../../services/usersTechniquesServices";

import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_TECHNIQUES_FILTERS,
  UV2_FICHES,
  dedupeTechniques,
  filterTechniques,
  getTechniquesStats,
  getUserIdFromLocalStorage,
  getUv2Fiches,
  sortTechniques,
} from "../utils/techniquesRevisionUtils";

export function useTechniquesRevisionPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [techniques, setTechniques] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_TECHNIQUES_FILTERS);
  const [selectedTechnique, setSelectedTechnique] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  const userId = useMemo(() => getUserIdFromLocalStorage(), []);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchFavorites = useCallback(async () => {
    if (!userId) {
      setFavoriteIds([]);
      return;
    }

    setFavoritesLoading(true);

    try {
      const favorites = await getUserFavorites(userId);
      const safeFavorites = Array.isArray(favorites) ? favorites : [];

      setFavoriteIds(
        safeFavorites.map((favorite) => favorite.technique_id).filter(Boolean),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des favoris :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des techniques favorites.",
        "error",
      );
    } finally {
      setFavoritesLoading(false);
    }
  }, [userId]);

  const fetchTechniques = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const response = await getRevisionTechniques({
          includeSelfDefense: filters.includeSelfDefense,
        });

        const safeTechniques = Array.isArray(response) ? response : [];
        const uniqueTechniques = dedupeTechniques(safeTechniques);

        setTechniques(sortTechniques(uniqueTechniques));

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Techniques actualisées.", "success");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des techniques :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des techniques.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters.includeSelfDefense],
  );

  useEffect(() => {
    fetchTechniques({ silent: true });
  }, [fetchTechniques]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const filteredTechniques = useMemo(() => {
    return filterTechniques(techniques, filters, favoriteIds);
  }, [techniques, filters, favoriteIds]);

  const stats = useMemo(() => {
    return getTechniquesStats(techniques, favoriteIds);
  }, [techniques, favoriteIds]);

  const uv2Fiches = useMemo(() => {
    return getUv2Fiches(techniques);
  }, [techniques]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [name]: value,
      };

      if (name === "mode") {
        if (value === "uv2") {
          nextFilters.uv2 = "all";
        }

        if (value !== "uv2") {
          nextFilters.uv2 = "all";
        }

        if (value !== "kata" && prev.mode === "kata") {
          nextFilters.famille = "all";
        }
      }

      return nextFilters;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_TECHNIQUES_FILTERS);
  }, []);

  const handleRandomUv2Fiche = useCallback(() => {
  const randomIndex = Math.floor(Math.random() * UV2_FICHES.length);
  const randomFiche = UV2_FICHES[randomIndex];

  setFilters((prev) => ({
    ...prev,
    mode: "uv2",
    uv2: randomFiche,
  }));
}, []);

  const handleToggleFavorite = useCallback(
    async (technique) => {
      if (!userId) {
        notificationRef.current?.(
          "Vous devez être connecté pour gérer vos favoris.",
          "error",
        );
        return;
      }

      if (!technique?.id) return;

      const isFavorite = favoriteIds.includes(technique.id);

      try {
        if (isFavorite) {
          await removeFavorite(userId, technique.id);

          setFavoriteIds((prev) => prev.filter((id) => id !== technique.id));

          notificationRef.current?.(
            "Technique retirée des favoris.",
            "success",
          );
        } else {
          await addFavorite(userId, technique.id);

          setFavoriteIds((prev) =>
            prev.includes(technique.id) ? prev : [...prev, technique.id],
          );

          notificationRef.current?.(
            "Technique ajoutée aux favoris.",
            "success",
          );
        }
      } catch (error) {
        console.error("Erreur lors de la mise à jour du favori :", error);

        notificationRef.current?.(
          "Erreur lors de la mise à jour du favori.",
          "error",
        );
      }
    },
    [favoriteIds, userId],
  );

  return {
    techniques,
    filteredTechniques,
    selectedTechnique,
    favoriteIds,
    filters,
    loading,
    refreshing,
    favoritesLoading,
    stats,
    uv2Fiches,
    fetchTechniques,
    setSelectedTechnique,
    handleFilterChange,
    resetFilters,
    handleToggleFavorite,
    handleRandomUv2Fiche,
  };
}
